import LegalPageShell from "@/components/site/LegalPageShell";
import { useLanguage, pick } from "@/lib/i18n";
import { SUPPORT_EMAIL } from "@/lib/translations";

const COPY = {
    en: {
        title: "Cookie Policy",
        subtitle:
            "This Cookie Policy explains what cookies and similar technologies are used on the Dino Detective website at dino-detective.app.",
        sections: [
            { id: "intro", label: "Introduction" },
            { id: "essential", label: "Essential cookies" },
            { id: "security", label: "Security cookies" },
            { id: "hosting", label: "Hosting-related cookies" },
            { id: "browser", label: "Browser cookies" },
            { id: "no-tracking", label: "No tracking or advertising" },
            { id: "choices", label: "Your choices" },
            { id: "contact", label: "Contact" },
        ],
    },
    de: {
        title: "Cookie-Richtlinie",
        subtitle:
            "Diese Cookie-Richtlinie erklärt, welche Cookies und ähnliche Technologien auf der Dino-Detective-Website unter dino-detective.app verwendet werden.",
        sections: [
            { id: "intro", label: "Einleitung" },
            { id: "essential", label: "Essenzielle Cookies" },
            { id: "security", label: "Sicherheits-Cookies" },
            { id: "hosting", label: "Hosting-Cookies" },
            { id: "browser", label: "Browser-Cookies" },
            { id: "no-tracking", label: "Kein Tracking oder Werbung" },
            { id: "choices", label: "Deine Wahlmöglichkeiten" },
            { id: "contact", label: "Kontakt" },
        ],
    },
};

export default function CookiePolicy() {
    const { lang } = useLanguage();
    const t = pick(COPY, lang);
    return (
        <LegalPageShell title={t.title} subtitle={t.subtitle} sections={t.sections}>
            {lang === "en" ? <EN /> : <DE />}
        </LegalPageShell>
    );
}

function EN() {
    return (
        <>
            <h2 id="intro">Introduction</h2>
            <p>
                The Dino Detective website is hosted on Emergent and connected to the custom domain <strong>dino-detective.app</strong>. We use
                only the cookies and similar local storage entries strictly necessary to make the site work properly, keep it secure, and remember
                non-personal preferences (such as language). We do <strong>not</strong> use advertising cookies, marketing cookies, Google
                Analytics or Meta Pixel.
            </p>

            <h2 id="essential">Essential cookies</h2>
            <p>
                Essential cookies (and similar local storage entries) are needed for the website to load and function. Without them, the site
                cannot be delivered correctly. Examples:
            </p>
            <ul>
                <li>Storing your language preference (EN / DE) so that the site loads in the language you chose.</li>
                <li>Remembering your cookie consent choice so we do not show the banner again on every page.</li>
            </ul>

            <h2 id="security">Security cookies</h2>
            <p>
                Security cookies and session tokens help protect the website against fraud, abuse and automated attacks. They are used by the
                hosting layer to keep the site available and safe for all visitors.
            </p>

            <h2 id="hosting">Hosting-related cookies</h2>
            <p>
                Our hosting provider (Emergent) may set a small number of technical cookies required to route traffic, balance load and prevent
                abuse. These cookies do not identify you personally and are not used to build advertising profiles.
            </p>

            <h2 id="browser">Browser cookies and local storage</h2>
            <p>
                Modern browsers offer cookies and local storage as ways to remember small pieces of information between page loads. The Dino
                Detective website uses local storage entries only for the strictly necessary purposes listed above (language, consent choice).
                You can clear cookies and local storage at any time using your browser settings.
            </p>

            <h2 id="no-tracking">No tracking or advertising</h2>
            <ul>
                <li>No Google Analytics.</li>
                <li>No Meta Pixel.</li>
                <li>No third-party advertising cookies.</li>
                <li>No re-marketing or behavioural targeting.</li>
                <li>No third-party social embeds that drop cookies on first load.</li>
            </ul>

            <h2 id="choices">Your choices</h2>
            <p>
                On your first visit, a clear consent banner appears at the bottom of the page with three options:
            </p>
            <ul>
                <li><strong>Accept All</strong> — accept all current essential cookies. (We do not use non-essential cookies today.)</li>
                <li><strong>Reject Non-Essential</strong> — keep only the essential cookies the site needs to function and stay secure.</li>
                <li><strong>Manage Preferences</strong> — open a small dialog with individual toggles for transparency.</li>
            </ul>
            <p>
                You can also delete cookies and local storage entries at any time from your browser settings.
            </p>

            <h2 id="contact">Contact</h2>
            <p>
                Questions about this Cookie Policy can be sent to <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
            </p>
        </>
    );
}

function DE() {
    return (
        <>
            <h2 id="intro">Einleitung</h2>
            <p>
                Die Dino-Detective-Website wird bei Emergent gehostet und ist mit der eigenen Domain <strong>dino-detective.app</strong>{" "}
                verbunden. Wir verwenden nur Cookies und vergleichbare lokale Speichereinträge, die für den Betrieb, die Sicherheit und das
                Merken nicht-personenbezogener Präferenzen (z. B. Sprache) zwingend nötig sind. Wir verwenden <strong>keine</strong>{" "}
                Werbe-Cookies, kein Google Analytics und kein Meta Pixel.
            </p>

            <h2 id="essential">Essenzielle Cookies</h2>
            <p>
                Essenzielle Cookies (und vergleichbare lokale Speichereinträge) sind nötig, damit die Website korrekt funktioniert:
            </p>
            <ul>
                <li>Speichern der gewählten Sprache (EN / DE).</li>
                <li>Merken deiner Cookie-Entscheidung, damit der Banner nicht auf jeder Seite erneut erscheint.</li>
            </ul>

            <h2 id="security">Sicherheits-Cookies</h2>
            <p>
                Sicherheits-Cookies und Session-Tokens schützen die Website vor Missbrauch und automatisierten Angriffen und werden auf der
                Hosting-Ebene eingesetzt.
            </p>

            <h2 id="hosting">Hosting-Cookies</h2>
            <p>
                Unser Hosting-Anbieter (Emergent) kann technisch erforderliche Cookies setzen, um Traffic zu leiten und Missbrauch zu vermeiden.
                Diese Cookies identifizieren dich nicht persönlich und dienen nicht zur Erstellung von Werbeprofilen.
            </p>

            <h2 id="browser">Browser-Cookies und lokaler Speicher</h2>
            <p>
                Moderne Browser bieten Cookies und Local Storage zum Speichern kleiner Informationen. Die Dino-Detective-Website verwendet
                Local-Storage-Einträge nur für die oben genannten zwingend erforderlichen Zwecke (Sprache, Einwilligung). Du kannst Cookies
                und Local Storage jederzeit in den Einstellungen deines Browsers löschen.
            </p>

            <h2 id="no-tracking">Kein Tracking oder Werbung</h2>
            <ul>
                <li>Kein Google Analytics.</li>
                <li>Kein Meta Pixel.</li>
                <li>Keine Werbe-Cookies Dritter.</li>
                <li>Kein Re-Marketing oder Behavioural Targeting.</li>
                <li>Keine Social-Embeds, die beim ersten Laden Cookies setzen.</li>
            </ul>

            <h2 id="choices">Deine Wahlmöglichkeiten</h2>
            <p>Beim ersten Besuch erscheint ein Einwilligungs-Banner am unteren Rand mit drei Optionen:</p>
            <ul>
                <li><strong>Alle akzeptieren</strong> — alle aktuellen essenziellen Cookies akzeptieren.</li>
                <li><strong>Nicht essenzielle ablehnen</strong> — nur das Nötigste behalten.</li>
                <li><strong>Einstellungen verwalten</strong> — Dialog mit einzelnen Schaltern zur Transparenz öffnen.</li>
            </ul>
            <p>Du kannst Cookies jederzeit in den Browsereinstellungen löschen.</p>

            <h2 id="contact">Kontakt</h2>
            <p>
                Fragen zu dieser Cookie-Richtlinie bitte an <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
            </p>
        </>
    );
}
